import { useState } from "react";
import { api } from "../../lib/api";
import { Download, PaperPlaneRight, Check, X, ArrowsClockwise, Link as LinkIcon } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const BASE = process.env.REACT_APP_BACKEND_URL;

function Card({ title, children, testid }) {
  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4" data-testid={testid}>
      <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">{title}</div>
      {children}
    </div>
  );
}

const STATUS_COLORS = { draft: "var(--c-muted)", in_review: "var(--c-amber)", approved: "var(--c-green)", rejected: "var(--c-red)",
  awaiting: "var(--c-amber)", accepted: "var(--c-green)", accepted_questions: "var(--c-accent)", constraint_raised: "var(--c-red)" };

export default function StatusPane({ stage, data, pid, user, busy, run }) {
  const [reviewComments, setReviewComments] = useState("");
  const [respText, setRespText] = useState({});
  const [portalEmail, setPortalEmail] = useState("");
  const [portalName, setPortalName] = useState("");
  const [showRevoked, setShowRevoked] = useState(false);
  const canReview = user && ["lead", "project_manager", "admin"].includes(user.role);
  const sheet = data.design_sheet;
  const pkg = data.approval_package;

  return (
    <div className="space-y-4" data-testid="status-pane">
      {(!stage || stage === 4) && (
        <Card title="Stage 4 · Design Sheet" testid="status-stage-4">
          {sheet ? (
            <>
              <div className="flex items-center gap-2 text-xs mb-3">
                <span className="text-[var(--c-muted)]">Status:</span>
                <span className="font-mono uppercase text-[10px] tracking-wider" style={{ color: STATUS_COLORS[sheet.status] }}
                  data-testid="sheet-status">{sheet.status.replace(/_/g, " ")}</span>
                <span className="text-[var(--c-muted)] font-mono text-[10px] ml-auto">Rev {sheet.rev}</span>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid="ds-export-xlsx"
                    onClick={() => window.open(`${BASE}/api/documents/${pid}/design-sheet/export.xlsx`, "_blank")}
                    className="flex-1 border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-7 text-[10px]">
                    <Download size={11} className="mr-1" /> Excel
                  </Button>
                  <Button size="sm" variant="outline" data-testid="ds-export-pdf"
                    onClick={() => window.open(`${BASE}/api/documents/${pid}/design-sheet/export.pdf`, "_blank")}
                    className="flex-1 border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-7 text-[10px]">
                    <Download size={11} className="mr-1" /> PDF
                  </Button>
                </div>
                <Button size="sm" variant="outline" data-testid="ds-regenerate-btn" disabled={!!busy}
                  onClick={() => run("ds", () => api.post(`/documents/${pid}/design-sheet/generate`))}
                  className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-7 text-[10px]">
                  <ArrowsClockwise size={11} className="mr-1" /> Regenerate from design
                </Button>
                {["draft", "rejected"].includes(sheet.status) && (
                  <Button size="sm" data-testid="ds-submit-btn" disabled={!!busy}
                    onClick={() => run("submit", () => api.post(`/documents/${pid}/design-sheet/submit`))}
                    className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-7 text-[10px]">
                    <PaperPlaneRight size={11} className="mr-1" /> Submit for review
                  </Button>
                )}
                {sheet.status === "in_review" && canReview && (
                  <div className="space-y-2 border-t border-[var(--c-border)] pt-2">
                    <Input data-testid="review-comments" value={reviewComments} onChange={(e) => setReviewComments(e.target.value)}
                      placeholder="Review comments…" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs" />
                    <div className="flex gap-2">
                      <Button size="sm" data-testid="ds-approve-btn" disabled={!!busy}
                        onClick={() => run("review", () => api.post(`/documents/${pid}/design-sheet/review`, { decision: "approved", comments: reviewComments }))}
                        className="flex-1 bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm h-7 text-[10px]">
                        <Check size={11} className="mr-1" /> Approve
                      </Button>
                      <Button size="sm" data-testid="ds-reject-btn" disabled={!!busy || reviewComments.trim().length < 5}
                        onClick={() => run("review", () => api.post(`/documents/${pid}/design-sheet/review`, { decision: "rejected", comments: reviewComments }))}
                        className="flex-1 bg-[var(--c-red)] hover:bg-[var(--c-red)]/85 text-[var(--c-text)] rounded-sm h-7 text-[10px]">
                        <X size={11} className="mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                )}
                {(sheet.reviews || []).map((r, i) => (
                  <div key={i} className="text-[10px] text-[var(--c-muted)] border-t border-[var(--c-border)]/50 pt-1.5" data-testid={`review-entry-${i}`}>
                    <span style={{ color: STATUS_COLORS[r.decision] }} className="font-mono uppercase">{r.decision}</span> by {r.by} · {String(r.at).slice(0, 10)}
                    {r.comments && <div className="mt-0.5 text-[var(--c-text)]">{r.comments}</div>}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <Button size="sm" data-testid="ds-generate-btn" disabled={!!busy || !data.has_design_results}
              onClick={() => run("ds", () => api.post(`/documents/${pid}/design-sheet/generate`))}
              className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
              Generate Design Sheet
            </Button>
          )}
          <div className="border-t border-[var(--c-border)] mt-3 pt-3">
            {data.design_output_notes ? (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" data-testid="notes-export-docx"
                  onClick={() => window.open(`${BASE}/api/documents/${pid}/design-output-notes/export.docx`, "_blank")}
                  className="flex-1 border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-7 text-[10px]">
                  <Download size={11} className="mr-1" /> Word
                </Button>
                <Button size="sm" variant="outline" data-testid="notes-export-pdf"
                  onClick={() => window.open(`${BASE}/api/documents/${pid}/design-output-notes/export.pdf`, "_blank")}
                  className="flex-1 border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-7 text-[10px]">
                  <Download size={11} className="mr-1" /> PDF
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" data-testid="notes-generate-btn" disabled={!!busy || !sheet}
                onClick={() => run("notes", () => api.post(`/documents/${pid}/design-output-notes/generate`))}
                className="w-full border-[var(--c-accent)]/50 text-[var(--c-accent)] hover:bg-[var(--c-accent)]/10 rounded-sm h-7 text-[10px]">
                Generate Design Output Notes (AI)
              </Button>
            )}
          </div>
        </Card>
      )}

      {(!stage || stage === 5) && (
        <Card title="Stage 5 · Handover Packs" testid="status-stage-5">
          {data.handover_packs.length === 0 ? (
            <Button size="sm" data-testid="handover-generate-btn" disabled={!!busy || !data.has_design_results}
              onClick={() => run("ho", () => api.post(`/documents/${pid}/handover/generate`))}
              className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
              Generate Handover Packs
            </Button>
          ) : (
            <div className="space-y-3">
              {data.handover_packs.map((p) => (
                <div key={p.id} className="border border-[var(--c-border)] rounded-sm p-3" data-testid={`pack-card-${p.id.slice(0, 8)}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[var(--c-text)]">{p.pack_name}</span>
                    <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: STATUS_COLORS[p.status] }}
                      data-testid={`pack-status-${p.id.slice(0, 8)}`}>{p.status.replace(/_/g, " ")}</span>
                  </div>
                  <div className="text-[10px] text-[var(--c-muted)] mt-1">To: {p.sent_to.map((t) => t.name).join(", ")}</div>
                  <Input data-testid={`pack-resp-text-${p.id.slice(0, 8)}`} value={respText[p.id] || ""}
                    onChange={(e) => setRespText((s) => ({ ...s, [p.id]: e.target.value }))}
                    placeholder="Questions / constraints…" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs mt-2" />
                  <div className="flex gap-1.5 mt-2">
                    <Button size="sm" data-testid={`pack-accept-${p.id.slice(0, 8)}`} disabled={!!busy}
                      onClick={() => run("ho", () => api.post(`/documents/${pid}/handover/${p.id}/respond`, { action: "accepted" }))}
                      className="flex-1 bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm h-7 text-[9px]">Accept</Button>
                    <Button size="sm" variant="outline" data-testid={`pack-questions-${p.id.slice(0, 8)}`} disabled={!!busy || (respText[p.id] || "").trim().length < 5}
                      onClick={() => run("ho", () => api.post(`/documents/${pid}/handover/${p.id}/respond`, { action: "questions", text: respText[p.id] }))}
                      className="flex-1 border-[var(--c-accent)]/50 text-[var(--c-accent)] rounded-sm h-7 text-[9px]">Questions</Button>
                    <Button size="sm" variant="outline" data-testid={`pack-constraint-${p.id.slice(0, 8)}`} disabled={!!busy || (respText[p.id] || "").trim().length < 5}
                      onClick={() => run("ho", () => api.post(`/documents/${pid}/handover/${p.id}/respond`, { action: "constraint_raised", text: respText[p.id] }))}
                      className="flex-1 border-[var(--c-red)]/50 text-[var(--c-red)] rounded-sm h-7 text-[9px]">Constraints</Button>
                  </div>
                  {p.responses.map((r, i) => (
                    <div key={i} className="text-[10px] text-[var(--c-muted)] mt-1.5 border-t border-[var(--c-border)]/50 pt-1" data-testid={`pack-response-${p.id.slice(0, 8)}-${i}`}>
                      <span style={{ color: STATUS_COLORS[r.action === "constraint_raised" ? "constraint_raised" : r.action === "questions" ? "accepted_questions" : "accepted"] }}
                        className="font-mono uppercase">{r.action.replace(/_/g, " ")}</span> by {r.name}{r.text ? ` — ${r.text}` : ""}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {(!stage || stage === 6) && (
        <Card title="Stage 6 · DO Notes" testid="status-stage-6">
          {data.do_notes.length === 0 ? (
            <Button size="sm" data-testid="donotes-generate-btn" disabled={!!busy || !data.has_design_results}
              onClick={() => run("dn", () => api.post(`/documents/${pid}/do-notes/generate`))}
              className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
              Generate & Distribute DO Notes
            </Button>
          ) : (
            <div className="space-y-2">
              {data.do_notes.map((n) => (
                <div key={n.id} className="flex items-center gap-2 border border-[var(--c-border)] rounded-sm px-3 py-2" data-testid={`note-card-${n.id.slice(0, 8)}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[var(--c-text)] truncate">{n.discipline}</div>
                    <div className="text-[10px] font-mono text-[var(--c-muted)]">{n.reads.length}/{n.sent_to.length} read</div>
                  </div>
                  {!n.reads.some((r) => r.user_id === user?.id) && (
                    <Button size="sm" variant="outline" data-testid={`note-read-${n.id.slice(0, 8)}`} disabled={!!busy}
                      onClick={() => run("dn", () => api.post(`/documents/${pid}/do-notes/${n.id}/read`))}
                      className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-6 text-[9px]">Mark read</Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {(!stage || stage === 7) && (
        <Card title="Stage 7 · Client Approval" testid="status-stage-7">
          {!pkg ? (
            <Button size="sm" data-testid="approval-generate-btn" disabled={!!busy || !data.has_design_results}
              onClick={() => run("ap", () => api.post(`/documents/${pid}/approval/generate`))}
              className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
              Generate Approval Package
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-mono text-[10px] text-[var(--c-muted)]">Rev {pkg.rev}</span>
                {pkg.client_status && (
                  <span className="font-mono text-[10px] uppercase tracking-wider"
                    style={{ color: pkg.client_status === "reject" ? "var(--c-red)" : "var(--c-green)" }} data-testid="client-status">
                    {pkg.client_status.replace(/_/g, " ")}
                  </span>
                )}
                <Button size="sm" variant="outline" data-testid="approval-revise-btn" disabled={!!busy}
                  onClick={() => run("ap", () => api.post(`/documents/${pid}/approval/revise`))}
                  className="ml-auto border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-6 text-[9px]">
                  <ArrowsClockwise size={10} className="mr-1" /> New revision
                </Button>
              </div>
              {pkg.rev_diff && (
                <div className="bg-[var(--c-amber)]/10 border border-[var(--c-amber)]/40 rounded-sm px-3 py-2" data-testid="rev-diff">
                  <div className="text-[10px] font-mono text-[var(--c-amber)] uppercase tracking-wider mb-1">
                    Rev {pkg.rev_diff.from_rev} → {pkg.rev_diff.to_rev} · {pkg.rev_diff.changes.length ? `${pkg.rev_diff.changes.length} change(s)` : "no spec changes"}
                  </div>
                  {pkg.rev_diff.changes.slice(0, 6).map((c, i) => (
                    <div key={i} className="text-[10px] font-mono text-[var(--c-muted)]">
                      {c.parameter}: <span className="text-[var(--c-red)]">{c.from}</span> → <span className="text-[var(--c-green)]">{c.to}</span>
                    </div>
                  ))}
                </div>
              )}
              {pkg.rejection_analysis && (
                <div className="bg-[var(--c-red)]/10 border border-[var(--c-red)]/40 rounded-sm px-3 py-2" data-testid="rejection-analysis">
                  <div className="text-[10px] font-mono text-[var(--c-red)] uppercase tracking-wider mb-1">AI rejection analysis</div>
                  <div className="text-[11px] text-[var(--c-text)] whitespace-pre-wrap">{pkg.rejection_analysis.summary}</div>
                  {(pkg.rejection_analysis.suggestions || []).map((s, i) => (
                    <div key={i} className="text-[10px] text-[var(--c-muted)] mt-1.5 border-t border-[var(--c-border)]/50 pt-1">
                      <span className="text-[var(--c-text)]">{s.label}</span>{s.impact ? ` — ${s.impact}` : ""}
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-2 border-t border-[var(--c-border)] pt-2">
                <Input data-testid="portal-email-input" type="email" value={portalEmail} onChange={(e) => setPortalEmail(e.target.value)}
                  placeholder="client@email.com" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs" />
                <Input data-testid="portal-name-input" value={portalName} onChange={(e) => setPortalName(e.target.value)}
                  placeholder="Reviewer name (optional)" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs" />
                <Button size="sm" data-testid="portal-send-btn" disabled={!!busy || !portalEmail.includes("@")}
                  onClick={() => run("send", async () => {
                    const { data: r } = await api.post(`/documents/${pid}/portal/send`, { client_email: portalEmail, reviewer_name: portalName });
                    const full = r.link.startsWith("http") ? r.link : `${window.location.origin}${r.link}`;
                    await navigator.clipboard.writeText(full).catch(() => {});
                    window.__lastPortalLink = full;
                  })}
                  className="w-full bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm h-7 text-[10px]">
                  <LinkIcon size={11} className="mr-1" /> Send to Client (copies link)
                </Button>
              </div>
              {(data.portal_links || []).length > 0 && (
                <div className="space-y-1.5" data-testid="portal-links-table">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-mono uppercase tracking-wider text-[var(--c-muted)]">Portal links</span>
                    <label className="flex items-center gap-1 text-[9px] font-mono text-[var(--c-muted)]">
                      <input type="checkbox" data-testid="show-revoked-toggle" checked={showRevoked}
                        onChange={(e) => setShowRevoked(e.target.checked)} className="accent-[var(--c-accent)]" /> show revoked
                    </label>
                  </div>
                  {data.portal_links.filter((l) => showRevoked || l.status === "active").map((l) => (
                    <div key={l._id} className="flex items-center gap-2 text-[10px] border border-[var(--c-border)] rounded-sm px-2.5 py-1.5"
                      data-testid={`portal-link-${l._id.slice(0, 8)}`}>
                      <span className="text-[var(--c-text)] truncate flex-1">{l.client_email}</span>
                      <span className="font-mono text-[var(--c-muted)]">Rev {l.rev}</span>
                      <span className={`font-mono uppercase ${l.status === "active" ? "text-[var(--c-green)]" : "text-[var(--c-red)]"}`}>{l.status}</span>
                      {l.response && <span className="font-mono text-[var(--c-accent)]">{l.response.action}</span>}
                      {l.status === "active" && (
                        <button data-testid={`revoke-link-${l._id.slice(0, 8)}`}
                          onClick={() => run("revoke", () => api.post(`/documents/${pid}/portal/revoke`, null, { params: { token_id: l._id } }))}
                          className="text-[var(--c-red)] hover:underline">revoke</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {(pkg.responses || []).length > 0 && (
                <div className="border-t border-[var(--c-border)] pt-2 space-y-1" data-testid="portal-responses">
                  {pkg.responses.map((r, i) => (
                    <div key={i} className="text-[10px] text-[var(--c-muted)]">
                      <span className="text-[var(--c-text)]">{r.name}</span> — <span className="font-mono uppercase"
                        style={{ color: r.action === "reject" ? "var(--c-red)" : "var(--c-green)" }}>{r.action.replace(/_/g, " ")}</span>
                      <span className="font-mono"> · Rev {r.rev}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
